import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { Sparkline } from "./Sparkline";

// 2026-10-30 — chat: "Stocks - Live Orders displays Today's profit. Add similar for
// Commodities Live orders". Realized P&L for TODAY from the Kite tradebook ledger
// (kite_ledger.py — real fills, net of charges): every account the viewer can see
// (admin: primary + linked; others: their own), with the Autopilot / copied /
// manual split. Closed round trips only, like the Stocks card; open positions are
// counted, not valued. The ledger captures fills every 10 min — Refresh pulls now.

const rs = (v) => (v == null || Number.isNaN(Number(v)) ? "—"
  : `${v >= 0 ? "+" : "−"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const col = (v) => (v == null ? undefined : v > 0 ? "var(--bull)" : v < 0 ? "var(--bear)" : undefined);
const istToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());

export const TodayLivePnl = ({ market = "commodities", testid = "today-live-pnl" }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTrades, setShowTrades] = useState(false);

  const load = useCallback(async () => {
    const day = istToday();
    try {
      const { data: d } = await api.get("/pnl/ledger", {
        params: { account: "all", market, from_date: day, to_date: day } });
      setData({ ...d, _day: day });
    } catch {
      setData(null);
    }
  }, [market]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/pnl/ledger/refresh");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  const view = useMemo(() => {
    if (!data || data.error) return null;
    const day = data._day;
    const trips = (data.trades || []).filter((t) => t.day === day)
      .sort((a, b) => String(a.exit_ts || "").localeCompare(String(b.exit_ts || "")));
    let run = 0;
    const series = trips.map((t) => (run += Number(t.net_rs || 0)));
    const byAcc = {};
    trips.forEach((t) => {
      const k = t.account_label || t.account;
      byAcc[k] = byAcc[k] || { net: 0, n: 0 };
      byAcc[k].net += Number(t.net_rs || 0);
      byAcc[k].n += 1;
    });
    const open = (data.open_positions || []).length;
    return { period: data.periods?.custom, trips, series, byAcc, open };
  }, [data]);

  if (!view) return null;
  const o = view.period?.overall || {};
  const total = o.net_rs ?? 0;
  const accounts = Object.entries(view.byAcc);

  return (
    <div className="border-grid surface p-4 space-y-3" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
          Realized P&amp;L Today · {market === "commodities" ? "MCX only" : "stocks / NFO only"} · {o.trades || 0} closed trade{o.trades === 1 ? "" : "s"}
          {view.open ? ` · ${view.open} open` : ""}
          <button onClick={refresh} disabled={busy} title="Pull today's Kite fills now"
                  className="text-muted-3 hover:text-muted-1 disabled:opacity-50">
            <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="font-mono-data text-2xl md:text-3xl font-semibold tracking-tight"
             style={{ color: col(total) }} data-testid={`${testid}-amount`}>
          {o.trades ? rs(total) : "₹0"}
          {o.trades ? (
            <span className="text-sm ml-2 font-normal text-muted-2">
              net · charges −₹{Math.round(o.charges_rs || 0).toLocaleString("en-IN")}
            </span>
          ) : null}
        </div>
        {o.trades ? (
          <div className="text-[10px] font-mono-data text-muted-2 flex flex-wrap gap-x-4">
            {accounts.length > 1 && accounts.map(([k, v]) => (
              <span key={k}>{k} <span style={{ color: col(v.net) }}>{rs(v.net)}</span> ({v.n}t)</span>
            ))}
            {(view.period?.by_group || []).map((g) => (
              <span key={g.group} className="text-muted-3">{g.label} <span style={{ color: col(g.net_rs) }}>{rs(g.net_rs)}</span></span>
            ))}
          </div>
        ) : (
          <div className="text-[10px] font-mono-data text-muted-3">
            No closed {market === "commodities" ? "MCX" : "stock (NFO)"} trades yet today (fills captured every 10 min — the refresh icon captures now).
            {market === "commodities" ? " Stock option trades show under Stocks → Live Orders." : " Commodity trades show under Commodities → Live Orders."}
          </div>
        )}
      </div>
      {view.series.length > 1 && (
        <Sparkline data={view.series} positive={total >= 0} width={90} height={28} />
      )}
    </div>
    {view.trips.length > 0 && (
      <div>
        <button onClick={() => setShowTrades((v) => !v)}
                className="text-[10px] uppercase tracking-[0.15em] font-mono-data text-muted-3 underline">
          {showTrades ? "Hide" : "Show"} today&apos;s trades ({view.trips.length})
        </button>
        {showTrades && (
          <table className="w-full text-[11px] font-mono-data mt-2" data-testid={`${testid}-trades`}>
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr className="border-b border-grid">
                <th className="text-left py-1">Exit</th>
                <th className="text-left">Account</th>
                <th className="text-left">Contract</th>
                <th className="text-left">Source</th>
                <th className="text-left">Reason</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Entry</th>
                <th className="text-right">Exit</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {[...view.trips].reverse().map((t, i) => (
                <tr key={`${t.account}-${t.tradingsymbol}-${t.exit_ts}-${i}`} className="border-b border-grid/40">
                  <td className="py-1 text-muted-3">{String(t.exit_ts || "").slice(11, 16)}</td>
                  <td className="text-muted-2">{t.account_label || t.account}</td>
                  <td>{t.tradingsymbol}{t.direction === "SHORT" ? " (short)" : ""}</td>
                  <td className="text-muted-2">{t.source_label}{t.estimated ? " · est." : ""}</td>
                  <td className="text-muted-3">{[t.entry_reason, t.exit_reason].filter(Boolean).join(" → ") || "—"}</td>
                  <td className="text-right">{t.qty}</td>
                  <td className="text-right">{Number(t.entry_price).toFixed(2)}</td>
                  <td className="text-right">{Number(t.exit_price).toFixed(2)}</td>
                  <td className="text-right" style={{ color: col(t.net_rs) }}>{rs(t.net_rs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    )}
    </div>
  );
};

export default TodayLivePnl;
